import * as React from 'react';
import { cn } from './utils';

/**
 * FileInput — labeled wrapper around a hidden native file input.
 *
 * The native `<input type="file">` is sr-only; the visible affordance is the
 * surrounding `<label>` styled like the rest of our form (bg-foreground/5
 * resting tint + hover step). This keeps the visual language consistent
 * with `<Input>` / `<Select>` while preserving the platform a11y story
 * (keyboard activation, screen-reader announcement of file selection).
 */
export interface FileInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'children'> {
  /** Visible label rendered inside the trigger surface. */
  triggerLabel: React.ReactNode;
  /** Optional className forwarded to the outer label wrapper. */
  wrapperClassName?: string;
  /** Disabled state — applies to both the trigger styles and the input. */
  disabled?: boolean;
}

export const FileInput = React.forwardRef<HTMLInputElement, FileInputProps>(
  ({ triggerLabel, wrapperClassName, className, disabled, ...props }, ref) => (
    <label
      className={cn(
        'inline-flex h-10 cursor-pointer items-center justify-center gap-2 rounded-md bg-foreground/5 px-4 text-sm font-medium text-foreground transition-colors hover:bg-foreground/10',
        disabled && 'cursor-not-allowed opacity-50',
        wrapperClassName,
      )}
    >
      <input
        ref={ref}
        type="file"
        disabled={disabled}
        className={cn('sr-only', className)}
        {...props}
      />
      <span>{triggerLabel}</span>
    </label>
  ),
);
FileInput.displayName = 'FileInput';
